import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, RagBadge } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Printer } from "lucide-react";

const TREND_ARROWS = { up: "↑", down: "↓" };

export default function WeeklyReportPage() {
  const { user } = useAuth();
  const canChoose = user?.function_scope === "Both";
  const [variant, setVariant] = useState(canChoose ? "Both" : user?.function_scope || "Both");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/reports/weekly", { params: { function: variant } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [variant]);

  const k = data?.kpis;
  const TITLES = {
    Both: "Group Weekly Review",
    Sales: "Sales Weekly Review",
    Service: "Service Weekly Review",
  };
  const title = TITLES[variant] || TITLES.Both;

  return (
    <div className="print-page" data-testid="weekly-review-report-page">
      <div className="no-print">
        <PageHeader title="Weekly Review Report" subtitle="Print-optimized — use Print to save as PDF for the review meeting">
          {canChoose && (
            <Select value={variant} onValueChange={setVariant}>
              <SelectTrigger className="h-9 w-[160px]" data-testid="report-variant-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Both">Group (Both)</SelectItem>
                <SelectItem value="Sales">Sales only</SelectItem>
                <SelectItem value="Service">Service only</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => window.print()} className="bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="weekly-review-print-button">
            <Printer size={15} className="mr-1.5" /> Print / Save PDF
          </Button>
        </PageHeader>
      </div>

      {loading || !data ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-64" />
        </div>
      ) : (
        <div className="bg-white border rounded-lg p-6 sm:p-8 print:border-0 print:p-0 print:rounded-none">
          {/* Report header */}
          <div className="print-section border-b-2 border-[hsl(var(--cx-navy))] pb-4 mb-5">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="font-serif text-3xl text-[hsl(var(--cx-navy))]">{title}</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Incheon Kia Dealership Group · Kerala · by {data.generated_by}
                </p>
                <p className="text-xs text-muted-foreground tabular" data-testid="weekly-report-stamps">
                  Generated on: {data.generated_on_fmt || dayjs(data.generated_at).format("DD MMM YYYY, h:mm A")}
                  {" · "}Based on data uploaded: {data.based_on_fmt || "—"}
                </p>
              </div>
              <div className="text-right">
                <p className="font-serif text-lg text-[hsl(var(--cx-gold))]">Incheon CX</p>
                <p className="text-[0.6rem] uppercase tracking-[0.18em] text-muted-foreground">Command Center</p>
              </div>
            </div>
          </div>

          {/* KPI strip */}
          <div className="print-section grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
            {[
              ["Total Open", k.total_open],
              ["Opened / Mo", k.opened_this_month],
              ["Closed / Mo", k.closed_this_month],
              ["Closure Rate", `${k.closure_rate}%`],
              ["Avg Res Days", k.avg_resolution_days],
              ["Red", k.red_count],
            ].map(([label, val], i) => (
              <div key={label} className={`border rounded-md p-3 text-center ${i === 5 ? "border-[hsl(var(--cx-crimson))] bg-[hsl(var(--status-red)/0.04)]" : ""}`}>
                <p className="kpi-value text-xl sm:text-2xl">{val}</p>
                <p className="text-[0.6rem] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* Escalation matrix summary */}
          {data.escalation_matrix && (
            <div className="print-section mb-6">
              <h2 className="font-serif text-lg mb-2 text-[hsl(var(--cx-navy))]">Escalation Matrix — {data.escalation_matrix.total} open escalated</h2>
              <div className="grid grid-cols-5 gap-2" data-testid="report-escalation-matrix">
                {data.escalation_matrix.levels.map((lv) => (
                  <div key={lv.level}
                    className={`border rounded-md p-3 text-center ${lv.level === 5 && lv.count > 0 ? "border-[hsl(var(--cx-crimson))] bg-[hsl(var(--status-red)/0.04)]" : ""}`}>
                    <p className="kpi-value text-xl">{lv.count.toLocaleString()}</p>
                    <p className="text-[0.6rem] uppercase tracking-wider text-muted-foreground mt-0.5">L{lv.level} · {lv.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Leaderboard */}
          <div className="print-section mb-6">
            <h2 className="font-serif text-lg mb-2 text-[hsl(var(--cx-navy))]">Outlet Leaderboard</h2>
            <table className="w-full text-xs border-collapse" data-testid="report-leaderboard-table">
              <thead>
                <tr className="border-y bg-[hsl(var(--cx-surface))]">
                  {["#", "Outlet", "Closure %", "Open", "Red", "Amber", "Green", "Avg Res", "Trend"].map((h) => (
                    <th key={h} className="py-1.5 px-2 text-left font-semibold uppercase tracking-wider text-[0.6rem] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.leaderboard.map((o) => (
                  <tr key={o.outlet_id} className="border-b">
                    <td className="py-1.5 px-2 tabular">{o.rank}</td>
                    <td className="py-1.5 px-2 font-medium">{o.outlet_name} <span className="text-muted-foreground">{o.outlet_code}</span></td>
                    <td className="py-1.5 px-2 tabular font-semibold">{o.closure_rate}%</td>
                    <td className="py-1.5 px-2 tabular">{o.open}</td>
                    <td className="py-1.5 px-2 tabular text-[hsl(var(--status-red))] font-semibold">{o.red}</td>
                    <td className="py-1.5 px-2 tabular text-[hsl(var(--status-amber))]">{o.amber}</td>
                    <td className="py-1.5 px-2 tabular text-[hsl(var(--status-green))]">{o.green}</td>
                    <td className="py-1.5 px-2 tabular">{o.avg_resolution_days}d</td>
                    <td className="py-1.5 px-2">{TREND_ARROWS[o.trend] || "→"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Outliers */}
          {data.outliers?.length > 0 && (
            <div className="print-section mb-6">
              <h2 className="font-serif text-lg mb-2 text-[hsl(var(--cx-navy))]">Outlier Watch</h2>
              <ul className="text-sm space-y-1">
                {data.outliers.slice(0, 6).map((o) => (
                  <li key={`${o.outlet_name}-${o.metric}-${o.function}`} className="flex gap-2">
                    <span className="text-[hsl(var(--cx-crimson))]">■</span>
                    <span>
                      <strong>{o.outlet_name}</strong> ({o.function}) —{" "}
                      {o.metric === "red_count" ? "red count" : "avg resolution days"} {o.value} vs group avg {o.group_avg}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Category breakdown */}
          <div className="print-section mb-6">
            <h2 className="font-serif text-lg mb-2 text-[hsl(var(--cx-navy))]">Top Categories</h2>
            <div className="space-y-1.5">
              {data.top_categories.map((c) => {
                const max = data.top_categories[0]?.count || 1;
                return (
                  <div key={c.category} className="flex items-center gap-2 text-xs">
                    <span className="w-44 truncate">{c.category}</span>
                    <div className="flex-1 bg-[hsl(var(--cx-surface-2))] rounded h-4 relative overflow-hidden border">
                      <div className="h-full bg-[hsl(var(--cx-navy))]" style={{ width: `${(c.count / max) * 100}%` }} />
                    </div>
                    <span className="w-12 text-right tabular">{c.count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Red complaint list */}
          <div className="print-section">
            <h2 className="font-serif text-lg mb-2 text-[hsl(var(--cx-crimson))]">
              Red Complaints — {data.red_complaints.length}{data.red_complaints.length >= 150 ? "+" : ""} open 5+ days
            </h2>
            <table className="w-full text-xs border-collapse" data-testid="report-red-list-table">
              <thead>
                <tr className="border-y bg-[hsl(var(--status-red)/0.05)]">
                  {["Complaint", "Outlet", "Customer", "Category", "Days", "Owner / SA", "Status"].map((h) => (
                    <th key={h} className="py-1.5 px-2 text-left font-semibold uppercase tracking-wider text-[0.6rem] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.red_complaints.slice(0, 60).map((c) => (
                  <tr key={c.id} className="border-b">
                    <td className="py-1 px-2 font-medium whitespace-nowrap">{c.complaint_number}</td>
                    <td className="py-1 px-2 whitespace-nowrap">{c.outlet_code}</td>
                    <td className="py-1 px-2 max-w-[140px] truncate">{c.customer_name}</td>
                    <td className="py-1 px-2 max-w-[140px] truncate">{c.category}</td>
                    <td className="py-1 px-2 tabular text-[hsl(var(--status-red))] font-semibold">{c.days_open}</td>
                    <td className="py-1 px-2 max-w-[120px] truncate">{c.current_owner || c.assigned_sa || "—"}</td>
                    <td className="py-1 px-2">{c.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.red_complaints.length > 60 && (
              <p className="text-xs text-muted-foreground mt-2">
                Showing top 60 by aging. Full list available in Complaints with the Red filter.
              </p>
            )}
          </div>

          <p className="text-[0.6rem] text-muted-foreground mt-8 pt-3 border-t">
            Confidential — Incheon Kia Dealership Group internal management review. Read-only: complaint data is managed in
            Salesforce and synced via the daily dump. RAG: Green 0–1d, Amber 2–4d, Red 5+d from date reported.
          </p>
        </div>
      )}
    </div>
  );
}
